<script setup lang="ts">
import { useHelper } from "~/composables/helper";
import type StatamicCollectionRequest from "~~/types/requests/StatamicCollectionRequest";
import type Career from "~~/types/Career";

const { getTaxonomyOptions } = useHelper();

const route = useRoute();
const config = useRuntimeConfig();
const loading = ref(false);

type CategoryListType = {
    slug: string;
    title: string;
    careers: Career[];
    additionalCareers: Career[];
};
const categories = ref<CategoryListType[]>([]);
const careers = ref<Career[]>([]);

async function performStatamicSearch(categorySlug = "", limit = 30, page = 1) {
    const params: { [k: string]: any } = {};
    params["limit"] = limit;
    params["page"] = page;
    params["sort"] = "-date_published"; // Get most recent posts

    if (categorySlug) {
        params["filter[category:contains]"] = categorySlug;
    }

    loading.value = true;
    const searchEndpoint = config.public.STATAMIC_API + "/collections/careers/entries";
    const { data: searchResult } = await useFetch<StatamicCollectionRequest>(searchEndpoint, {
        params: params,
    }).finally(() => {
        loading.value = false;
    });

    return searchResult.value;
}

async function populateCareers() {
    const result = await performStatamicSearch();
    if (!result) {
        return;
    }

    careers.value = result.data;
}

function filterCareers(categorySlug: string) {
    return careers.value.filter((career) => {
        return career.category.slug.includes(categorySlug);
    });
}

async function populateCategories() {
    const taxonomies = (await getTaxonomyOptions("career_categories")) as { key: string; value: string }[];
    if (!taxonomies || taxonomies.length === 0) {
        return;
    }

    taxonomies.forEach((category) => {
        const categoryCareers = filterCareers(category.key);
        categories.value.push({
            slug: category.key,
            title: category.value,
            careers: categoryCareers ? categoryCareers.slice(0, 2) : [],
            additionalCareers: categoryCareers ? categoryCareers.slice(3) : [],
        });
    });
}

await populateCareers();
await populateCategories();
</script>

<template>
    <div class="bg-abc-gray">
        <div
            v-if="categories.length > 0"
            class="container pt-10 pb-20"
        >
            <H2Heading class="text-center">We Are Hiring</H2Heading>
            <div class="mx-auto lg:w-3/4 xl:w-1/2">
                <div
                    v-for="category of categories"
                    :key="category.slug"
                    class="pb-8"
                >
                    <H3Heading>{{ category.title }}</H3Heading>
                    <div
                        v-for="career of category.careers"
                        :key="career.id"
                        class="border-t py-2 hover:bg-gray-200 md:flex"
                    >
                        <NuxtLink
                            :to="`/careers/${career.slug}`"
                            class="text-abc-blue flex-1"
                        >
                            {{ career.title }}
                        </NuxtLink>
                        <div class="flex-none">{{ career.city }}, {{ career.state.title }}</div>
                    </div>

                    <div
                        v-if="category.additionalCareers.length > 0"
                        class="text-center"
                    >
                        <ButtonSecondary
                            py="py-0.5"
                            @click="
                                category.careers = [...category.careers, ...category.additionalCareers];
                                category.additionalCareers = [];
                            "
                        >
                            View Additional Positions
                        </ButtonSecondary>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-else
            class="py-10 text-center"
        >
            Sorry, there are no openings available at this time....
        </div>
    </div>
</template>

<style scoped></style>
